// Screen-space world overlay for the 3D renderer. The 3D scene draws the battlefield and
// glowing effects; this pass draws the flat, always-legible gameplay UI that used to live
// in the 2D world renderer — selection rings, per-squad health/morale/suppression bars,
// model pips, posture tags (BROKEN / FRENZY / PINNED / DUG IN), the cover-shield move
// preview, the formation-facing drag arrow, strategic-point labels, and floating combat
// text — by projecting sim world coords to screen through the camera. Keeping these as a
// crisp 2D overlay (not 3D geometry) preserves the readability rule in GDD §7.1.

import { coverPreviewAt } from "../sim/cover";
import type { Faction, Squad } from "../sim/types";
import type { World } from "../sim/world";
import type { ICamera } from "../render/camera";
import type { ViewState } from "../render/renderer";
import type { Vec2 } from "../engine/vec";

const OWNER_COLOR: Record<string, string> = { legion: "#c33", kindled: "#e0771b", neutral: "#8a8577" };

export class WorldOverlay {
  private ctx: CanvasRenderingContext2D;
  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  /** Pixels-per-world-unit at ground point p (for perspective-correct UI sizing). */
  private pxScale(cam: ICamera, p: Vec2): number {
    const a = cam.worldToScreen(p);
    const b = cam.worldToScreen({ x: p.x + 40, y: p.y });
    const c = cam.worldToScreen({ x: p.x, y: p.y + 40 });
    const sx = Math.hypot(b.x - a.x, b.y - a.y) / 40;
    const sy = Math.hypot(c.x - a.x, c.y - a.y) / 40;
    return Math.max(0.18, (sx + sy) / 2);
  }

  private interp(prev: Vec2, cur: Vec2, a: number): Vec2 {
    return { x: prev.x + (cur.x - prev.x) * a, y: prev.y + (cur.y - prev.y) * a };
  }

  draw(world: World, view: ViewState, cam: ICamera): void {
    this.drawPointLabels(world, cam);
    this.drawStructures(world, cam);
    this.drawSelection(world, view, cam);
    if (view.showMovePreview && view.hoverWorld) this.drawMovePreview(world, view, cam);
    if (view.facingDrag) this.drawFacingDrag(view.facingDrag, cam);
    for (const sq of world.squads) if (!sq.dead) this.drawSquadStatus(sq, view, cam);
    this.drawText(world, cam);
  }

  private drawStructures(world: World, cam: ICamera): void {
    for (const s of world.structures) {
      if (s.hp >= s.maxHp && s.buildProgress >= 1) continue;
      const p = cam.worldToScreen(s.pos);
      const w = 46 * Math.min(1.4, Math.max(0.6, this.pxScale(cam, s.pos)));
      const y = p.y - 44;
      if (s.hp < s.maxHp) this.bar(p.x, y, w, 5, s.hp / s.maxHp, s.faction === "legion" ? "#6fcf6f" : "#e0771b");
      if (s.buildProgress < 1) this.bar(p.x, y, w, 5, s.buildProgress, "#7fd0ff");
    }
  }

  private drawPointLabels(world: World, cam: ICamera): void {
    const ctx = this.ctx;
    ctx.textAlign = "center";
    for (const p of world.points) {
      const s = cam.worldToScreen(p.pos);
      ctx.fillStyle = "rgba(230,224,212,0.9)";
      ctx.font = "600 12px Trebuchet MS, sans-serif";
      ctx.fillText(`${p.name}${p.isPower ? "  ⚡" : ""}`, s.x, s.y + 30);
      if (p.captureProgress > 0 && p.captureProgress < 1) {
        ctx.fillStyle = OWNER_COLOR[p.capturing];
        ctx.font = "700 11px Trebuchet MS";
        ctx.fillText(`${Math.round(p.captureProgress * 100)}%`, s.x, s.y + 44);
      }
    }
    ctx.textAlign = "left";
  }

  private drawSelection(world: World, view: ViewState, cam: ICamera): void {
    const ctx = this.ctx;
    for (const sq of world.squads) {
      if (sq.dead || !sq.selected) continue;
      const ring = sq.faction === view.playerFaction ? "rgba(120,220,120,0.9)" : "rgba(220,90,90,0.9)";
      ctx.strokeStyle = ring;
      ctx.lineWidth = 2;
      for (const m of sq.models) {
        if (!m.alive) continue;
        const w = this.interp(m.prevPos, m.pos, view.alpha);
        const s = cam.worldToScreen(w);
        const r = 14 * this.pxScale(cam, w);
        ctx.beginPath();
        ctx.ellipse(s.x, s.y + r * 0.3, r, r * 0.5, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  private drawSquadStatus(sq: Squad, view: ViewState, cam: ICamera): void {
    const ctx = this.ctx;
    let top: Vec2 | null = null;
    let anchorW: Vec2 | null = null;
    let hpSum = 0;
    let hpMax = 0;
    for (const m of sq.models) {
      if (!m.alive) continue;
      const w = this.interp(m.prevPos, m.pos, view.alpha);
      const s = cam.worldToScreen(w);
      if (!top || s.y < top.y) {
        top = s;
        anchorW = w;
      }
      hpSum += m.hp;
      hpMax += m.maxHp;
    }
    if (!top || !anchorW) return;
    const scale = this.pxScale(cam, anchorW);
    const isVehicle = sq.def.role === "vehicle";
    const w = (isVehicle ? 46 : 36) * Math.min(1.4, Math.max(0.55, scale));
    const bx = top.x - w / 2;
    const lift = (isVehicle ? 30 : 22) * Math.min(1.5, Math.max(0.7, scale)) + 6;
    const by = top.y - lift;
    const own = sq.faction === view.playerFaction;
    this.bar(top.x, by, w, 4, hpMax ? hpSum / hpMax : 0, own ? "#6fcf6f" : "#d05555");
    const mc = sq.broken ? "#d0483c" : "#e0b83c";
    this.bar(top.x, by + 6, w, 3, sq.morale / sq.maxMorale, mc);
    if (sq.suppression > 0.05) {
      ctx.fillStyle = "rgba(180,180,190,0.75)";
      ctx.fillRect(bx, by + 11, w * Math.min(1, sq.suppression), 2);
    }
    // model pips
    const alive = sq.models.filter((m) => m.alive).length;
    const pipW = Math.max(3, w / Math.max(6, sq.def.modelCount));
    for (let i = 0; i < sq.def.modelCount; i++) {
      ctx.fillStyle = i < alive ? "rgba(230,224,212,0.9)" : "rgba(120,110,100,0.5)";
      ctx.fillRect(bx + i * pipW, by - 5, pipW - 1, 3);
    }
    // posture tag
    let tag = "";
    if (sq.frenzy) tag = "FRENZY";
    else if (sq.broken) tag = "BROKEN";
    else if (sq.suppression >= 0.72) tag = "PINNED";
    else if (sq.meleeLock.size) tag = "MELEE";
    else if (sq.discipline > 0.5) tag = "DUG IN";
    if (tag) {
      ctx.font = "700 10px Trebuchet MS";
      ctx.textAlign = "center";
      ctx.fillStyle = sq.frenzy ? "#ff8a2c" : sq.broken ? "#ff6a5a" : sq.suppression >= 0.72 ? "#cfcfe0" : sq.meleeLock.size ? "#ffd24a" : "#ffe089";
      ctx.fillText(tag, top.x, by - 9);
      ctx.textAlign = "left";
    }
  }

  private bar(cx: number, y: number, w: number, h: number, frac: number, color: string): void {
    const ctx = this.ctx;
    const x = cx - w / 2;
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(x - 1, y - 1, w + 2, h + 2);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w * Math.max(0, Math.min(1, frac)), h);
  }

  private drawMovePreview(world: World, view: ViewState, cam: ICamera): void {
    const ctx = this.ctx;
    const p = view.hoverWorld!;
    const s = cam.worldToScreen(p);
    const scale = this.pxScale(cam, p);
    const pips = coverPreviewAt(world, p, view.playerFaction);
    ctx.strokeStyle = "rgba(120,220,120,0.75)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(s.x, s.y, 16 * scale, 8 * scale, 0, 0, Math.PI * 2);
    ctx.stroke();
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = i < pips ? "#8fd8ff" : "rgba(120,120,120,0.4)";
      const px = s.x - 14 + i * 14;
      const py = s.y - 34;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + 5, py + 3);
      ctx.lineTo(px + 5, py + 10);
      ctx.lineTo(px, py + 13);
      ctx.lineTo(px - 5, py + 10);
      ctx.lineTo(px - 5, py + 3);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawFacingDrag(fd: { from: Vec2; to: Vec2 }, cam: ICamera): void {
    const ctx = this.ctx;
    const a = cam.worldToScreen(fd.from);
    const b = cam.worldToScreen(fd.to);
    ctx.strokeStyle = "rgba(120,220,120,0.85)";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.setLineDash([]);
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    ctx.fillStyle = "rgba(120,220,120,0.9)";
    ctx.save();
    ctx.translate(b.x, b.y);
    ctx.rotate(ang);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-12, -6);
    ctx.lineTo(-12, 6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private drawText(world: World, cam: ICamera): void {
    const ctx = this.ctx;
    ctx.textAlign = "center";
    for (const e of world.effects) {
      if (e.kind !== "text") continue;
      const k = e.t / e.ttl;
      const s = cam.worldToScreen(e.a);
      ctx.fillStyle = (e.faction as Faction) === "kindled" ? `rgba(255,150,60,${1 - k})` : `rgba(255,225,150,${1 - k})`;
      ctx.font = "700 15px Trebuchet MS";
      ctx.fillText(e.text ?? "", s.x, s.y - 20 - k * 24);
    }
    ctx.textAlign = "left";
  }
}
